import { useSyncExternalStore } from 'react'
import { DEFAULT_PROMOTION, type PromotionRule } from './signal-model'

/**
 * Per-scope signal settings. decision S6 superseded by S34: the rule scores
 * connected-signals subgraphs (total_weight); schema 2 drops a stored
 * schema-1 rule. localStorage, one key per cluster+scope:
 * `bloo.signal-settings.<clusterId>.<scopeId>`. Same house rules as the
 * other local stores: try-catch, SSR-safe, tolerant read.
 */

export interface SignalSettings {
  promotion: PromotionRule
  /** Entity keys (signal-model entityKey) the analyst is watching. */
  watchlist: string[]
}

export const SETTINGS_SCHEMA = 2
export const DEFAULT_SETTINGS: SignalSettings = { promotion: DEFAULT_PROMOTION, watchlist: [] }

export function settingsStorageKey(clusterId: string, scopeId: string): string {
  return `bloo.signal-settings.${clusterId}.${scopeId}`
}

export function readSettings(v: unknown): SignalSettings {
  if (typeof v !== 'object' || v === null) return DEFAULT_SETTINGS
  const o = v as Record<string, unknown>
  const current = o.schema === SETTINGS_SCHEMA
  const p = (o.promotion ?? {}) as Record<string, unknown>
  const num = (x: unknown, d: number) => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? x : d)
  return {
    promotion: current
      ? {
          scoreThreshold: num(p.scoreThreshold, DEFAULT_PROMOTION.scoreThreshold),
          tacticThreshold: num(p.tacticThreshold, DEFAULT_PROMOTION.tacticThreshold),
        }
      : DEFAULT_PROMOTION,
    watchlist: Array.isArray(o.watchlist) ? o.watchlist.filter((k): k is string => typeof k === 'string') : [],
  }
}

interface Store {
  get: () => SignalSettings
  set: (patch: Partial<SignalSettings>) => void
  toggleWatch: (entityKey: string) => void
  subscribe: (cb: () => void) => () => void
}

const stores = new Map<string, Store>()

export function signalSettingsStore(clusterId: string, scopeId: string): Store {
  const key = settingsStorageKey(clusterId, scopeId)
  let s = stores.get(key)
  if (s) return s
  let cache: SignalSettings | null = null
  const listeners = new Set<() => void>()
  const read = (): SignalSettings => {
    if (cache) return cache
    try {
      const raw = globalThis.localStorage?.getItem(key)
      cache = readSettings(raw ? JSON.parse(raw) : null)
    } catch {
      cache = DEFAULT_SETTINGS
    }
    return cache
  }
  const write = (next: SignalSettings) => {
    cache = next
    try {
      globalThis.localStorage?.setItem(key, JSON.stringify({ schema: SETTINGS_SCHEMA, ...next }))
    } catch {
      // Settings are a convenience; a refused write keeps the in-memory value.
    }
    for (const cb of listeners) cb()
  }
  s = {
    get: read,
    set: (patch) => write({ ...read(), ...patch }),
    toggleWatch: (k) => {
      const cur = read()
      const has = cur.watchlist.includes(k)
      write({ ...cur, watchlist: has ? cur.watchlist.filter((x) => x !== k) : [...cur.watchlist, k] })
    },
    subscribe: (cb) => {
      listeners.add(cb)
      return () => {
        listeners.delete(cb)
      }
    },
  }
  stores.set(key, s)
  return s
}

/** Live settings for a scope. */
export function useSignalSettings(clusterId: string, scopeId: string): { settings: SignalSettings; store: Store } {
  const store = signalSettingsStore(clusterId, scopeId)
  const settings = useSyncExternalStore(store.subscribe, store.get, store.get)
  return { settings, store }
}
